import React, { useState, useEffect } from 'react';
import {
  Typography,
  FormControlLabel,
  Switch,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import SettingsPageLayout from './SettingsPageLayout';
import { useSettingsForm } from '../SettingsFormContext';

const STORAGE_KEY = 'beck_settings_notifications';

const defaultPrefs = {
  emailTaskDigest: true,
  emailClientMessages: true,
  emailBillingAlerts: true,
  inAppAll: true,
};

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...defaultPrefs };
    return { ...defaultPrefs, ...JSON.parse(raw) };
  } catch {
    return { ...defaultPrefs };
  }
};

const NotificationsSettings = () => {
  const { setIsDirty } = useSettingsForm();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [snackbar, setSnackbar] = useState({ open: false });

  useEffect(() => {
    setPrefs(loadPrefs());
  }, []);

  const update = (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    setIsDirty(false);
    setSnackbar({ open: true, message: 'Notification preferences saved', severity: 'success' });
  };

  const handleCancel = () => {
    setPrefs(loadPrefs());
    setIsDirty(false);
  };

  return (
    <>
      <SettingsPageLayout
        title="Notifications"
        description="Choose how you want to be notified about work in Firmflow. These preferences apply to your account only."
        onSave={handleSave}
        onCancel={handleCancel}
      >
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
          Email
        </Typography>
        <Stack spacing={1} sx={{ pt: 0.5 }}>
          <FormControlLabel
            control={
              <Switch
                checked={prefs.emailTaskDigest}
                onChange={(e) => update('emailTaskDigest', e.target.checked)}
                color="primary"
              />
            }
            label="Task and deadline summaries"
          />
          <FormControlLabel
            control={
              <Switch
                checked={prefs.emailClientMessages}
                onChange={(e) => update('emailClientMessages', e.target.checked)}
                color="primary"
              />
            }
            label="Client portal messages"
          />
          <FormControlLabel
            control={
              <Switch
                checked={prefs.emailBillingAlerts}
                onChange={(e) => update('emailBillingAlerts', e.target.checked)}
                color="primary"
              />
            }
            label="Billing and subscription alerts"
          />
        </Stack>
        <Typography variant="subtitle2" color="text.secondary" fontWeight={600} sx={{ pt: 2 }}>
          In-app
        </Typography>
        <FormControlLabel
          control={
            <Switch checked={prefs.inAppAll} onChange={(e) => update('inAppAll', e.target.checked)} color="primary" />
          }
          label="Show alerts in the notification center"
        />
        <Typography variant="caption" color="text.secondary">
          Preferences are stored on this browser.
        </Typography>
      </SettingsPageLayout>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity || 'success'} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationsSettings;
